<template>
  <BaseModal :modalTitle="$t('W: Planlæg afsendelse')">
    <!-- Body in the default slot with min-h-20, matching ConfirmationModal and
         UnsavedChangesModal so every confirmation in the app has the same
         height and spacing. -->
    <div class="mb-3 min-h-20">
      <p>
        {{ $t('W: Rapporten godkendes nu og sendes automatisk til kunden:') }}
      </p>
      <p class="mt-1 font-semibold">
        {{ $d(sendAtMs, 'shortMonthDateTime') }}
      </p>
      <!-- Only shown when the send day is not the deadline day. A deadline
           before 08:00 cannot be met by that morning's send, so it moves to the
           previous working day — the one case where the date above is not the
           one the user expects from the delivery row, and therefore the one
           worth spelling out rather than leaving them to notice. -->
      <p
        v-if="isMovedEarlier"
        class="mt-3 text-sm text-text-secondary">
        {{
          $t(
            'W: Svarfristen er {deadline}. Den ligger før kl. 08.00, så rapporten sendes den foregående arbejdsdag.',
            { deadline: $d(deadlineMs, 'shortMonthDateTime') }
          )
        }}
      </p>
    </div>

    <template #buttons>
      <UiBaseButton
        :text="$t('action.cancel')"
        theme="neutral"
        @button-click="onCancelClick" />
      <UiBaseButton
        :text="$t('action.continue')"
        theme="primary"
        @button-click="onConfirmClick" />
    </template>
  </BaseModal>
</template>

<script lang="ts">
import type { PropType } from 'vue'

/**
 * Names the day a delivery approved with "Godkend og planlæg" will actually go out, before the
 * PIN step locks it in.
 *
 * Built like UnsavedChangesModal rather than reusing ConfirmationModal, because the caller opens
 * the PIN modal on the answer: the callback is invoked and `close` emitted **synchronously**, so
 * this dialog has popped itself off the stack by the time the awaiting caller resumes. Going
 * through ConfirmationModal, which awaits its `onConfirm` before emitting `close`, would let the
 * caller push the PIN modal first and have this one's close pop the PIN modal instead of itself.
 */
export default {
  name: 'ConfirmScheduledSendModal',
  emits: ['close'],
  props: {
    /** When the scheduled send will fire — resolved by `resolveScheduledSend`. */
    sendAtMs: { type: Number, required: true },
    /** The delivery's own TAT deadline, shown only when the two differ. */
    deadlineMs: { type: Number, required: true },
    /** True when the deadline falls before 08:00 and the send moved a working day earlier. */
    isMovedEarlier: { type: Boolean, default: false },
    onConfirm: { type: Function as PropType<() => void>, required: true },
    onCancel: { type: Function as PropType<() => void>, required: true },
  },
  data() {
    return {
      hasAnswered: false,
    }
  },
  methods: {
    onConfirmClick(): void {
      this.hasAnswered = true
      this.onConfirm()
      this.$emit('close')
    },
    onCancelClick(): void {
      this.hasAnswered = true
      this.onCancel()
      this.$emit('close')
    },
  },
  beforeUnmount() {
    // ModalHost renders its own X and lets Escape dismiss this dialog, so it can disappear without
    // either button being pressed. The caller is awaiting an answer either way — without this
    // fallback a dismissed dialog would leave the approve flow hanging with nothing on screen.
    if (!this.hasAnswered) {
      this.onCancel()
    }
  },
}
</script>
